import { Sparkles, BookOpenText, TrendingUp } from "lucide-react";

const ICONS = [TrendingUp, Sparkles, BookOpenText];

function Sparkline({ points }) {
  if (!points || points.length < 2) return null;
  const w = 220;
  const h = 44;
  const pad = 4;
  const values = points.map((p) => Number(p.value) || 0);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const stepX = (w - pad * 2) / (points.length - 1);
  const coords = values.map((v, i) => {
    const x = pad + i * stepX;
    const y = pad + (h - pad * 2) * (1 - (v - min) / span);
    return [x, y];
  });
  const path = coords.map((c, i) => (i === 0 ? `M${c[0]},${c[1]}` : `L${c[0]},${c[1]}`)).join(" ");
  const area = `${path} L${coords[coords.length - 1][0]},${h - pad} L${coords[0][0]},${h - pad} Z`;
  const last = coords[coords.length - 1];
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-10 mt-3 relative z-10" preserveAspectRatio="none" data-testid="highlight-sparkline">
      <path d={area} fill="rgba(148, 163, 184, 0.18)" />
      <path d={path} fill="none" stroke="#E2E8F0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={last[0]} cy={last[1]} r="2.5" fill="#fff" />
    </svg>
  );
}

export default function Highlights({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="highlights">
      {items.map((h, idx) => {
        const Icon = ICONS[idx % ICONS.length];
        const first = h.points?.[0]?.value ?? 0;
        const last = h.points?.[h.points.length - 1]?.value ?? 0;
        const delta = last - first;
        const pct = first > 0 ? Math.round((delta / first) * 100) : null;
        const trendUp = delta > 0;
        return (
          <div
            key={h.indicator_id}
            className="bg-slate-900 text-white p-6 rounded-sm border border-slate-900 relative overflow-hidden"
            data-testid={`highlight-${idx}`}
          >
            <Icon className="absolute -right-2 -bottom-2 w-24 h-24 text-slate-800" strokeWidth={1} />
            <p className="label-overline text-slate-400">Featured impact</p>
            <div className="font-heading text-4xl sm:text-5xl tracking-tight mt-3 tabular relative z-10">
              {h.value_display}
              {h.unit && <span className="text-base text-slate-300 ml-2 font-normal align-baseline">{h.unit}</span>}
            </div>
            <p className="mt-3 text-sm text-slate-200 relative z-10">{h.label}</p>
            {h.caption && (
              <p className="mt-2 text-xs italic text-slate-300 relative z-10 leading-relaxed" data-testid={`highlight-caption-${idx}`}>
                {h.caption}
              </p>
            )}
            <Sparkline points={h.points} />
            {h.target && (
              <ProgressBar target={h.target} />
            )}
            <div className="flex items-center justify-between mt-2 relative z-10">
              {h.period_range ? (
                <p className="label-overline text-slate-500">{h.period_range}</p>
              ) : <span/>}
              {h.points?.length >= 2 && pct !== null && (
                <span className={`text-xs font-mono ${trendUp ? "text-emerald-500" : delta < 0 ? "text-red-400" : "text-slate-400"}`} data-testid={`highlight-delta-${idx}`}>
                  {trendUp ? "▲" : delta < 0 ? "▼" : "■"} {pct >= 0 ? "+" : ""}{pct}%
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ProgressBar({ target }) {
  const pct = Math.min(150, Math.max(0, target.progress_pct || 0));
  const clamped = Math.min(100, pct);
  const overflow = pct > 100;
  const color =
    target.status === "met" ? "bg-emerald-500" :
    target.status === "on_track" ? "bg-sky-400" :
    "bg-amber-400";
  return (
    <div className="mt-3 relative z-10" data-testid="highlight-progress">
      <div className="flex items-center justify-between text-[10px] tracking-wider uppercase text-slate-400 mb-1">
        <span>{overflow ? "Target exceeded" : "vs. target"}</span>
        <span className="font-mono text-slate-300">
          {target.progress_pct}% of {target.mode === "per_period" ? "elapsed" : "total"}
        </span>
      </div>
      <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${clamped}%` }} />
      </div>
    </div>
  );
}
